/**
 * Utility functions for parsing and extracting data from JWT tokens
 */

export interface OrganizationInfo {
  alias: string;
  id: string;
  databaseName: string;
  inputBucket: string;
  outputBucket: string;
  workspaceStatus: string;
  hasWorkspace: boolean;
  appLicenses: Array<{ appId: string }>;
  isOrgAdmin: boolean;
}

export interface TokenClaims {
  exp: number;
  iat: number;
  sub: string;
  name?: string;
  email?: string;
  preferred_username?: string;
  organization?: Record<string, {
    id?: string[];
    databaseName?: string[];
    inputBucket?: string[];
    outputBucket?: string[];
    workspaceStatus?: string[];
    hasWorkspace?: string[];
    'app-licenses'?: string[];
    'app_licenses'?: string[];
    'org-admins'?: string[];
  }>;
  realm_access?: {
    roles: string[];
  };
  resource_access?: Record<string, { roles: string[] }>;
  scope?: string;
}

/**
 * Decode a JWT token and extract the payload claims
 */
export function decodeToken(token: string): TokenClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format: expected 3 parts, got', parts.length);
      return null;
    }
    
    // Decode base64url to base64, then decode
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload) as TokenClaims;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

/**
 * Extract organization information from the token
 * Returns the first organization found (user's primary org)
 */
export function getOrganizationFromToken(token: string): OrganizationInfo | null {
  const claims = decodeToken(token);
  if (!claims || !claims.organization) {
    console.warn('No organization claim found in token');
    return null;
  }
  
  const orgEntries = Object.entries(claims.organization);
  if (orgEntries.length === 0) {
    console.warn('Organization claim is empty');
    return null;
  }
  
  // Get the first (and typically only) organization
  const [alias, orgData] = orgEntries[0];
  
  // Parse app licenses from JSON string array
  let appLicenses: Array<{ appId: string }> = [];
  const appLicensesRaw = orgData['app-licenses']?.[0] || orgData['app_licenses']?.[0];
  if (appLicensesRaw) {
    try {
      appLicenses = JSON.parse(appLicensesRaw);
    } catch (e) {
      console.warn('Failed to parse app-licenses:', e);
    }
  }
  
  // Check if current user is an org admin
  let isOrgAdmin = false;
  const orgAdminsRaw = orgData['org-admins']?.[0];
  if (orgAdminsRaw && claims.sub) {
    try {
      const orgAdmins = JSON.parse(orgAdminsRaw);
      isOrgAdmin = orgAdmins.includes(claims.sub);
    } catch (e) {
      console.warn('Failed to parse org-admins:', e);
    }
  }
  
  return {
    alias,
    id: orgData.id?.[0] || '',
    databaseName: orgData.databaseName?.[0] || '',
    inputBucket: orgData.inputBucket?.[0] || '',
    outputBucket: orgData.outputBucket?.[0] || '',
    workspaceStatus: orgData.workspaceStatus?.[0] || '',
    hasWorkspace: orgData.hasWorkspace?.[0] === 'true',
    appLicenses,
    isOrgAdmin,
  };
}

/**
 * Get the organization alias from a token (shorthand for getOrganizationFromToken)
 */
export function getOrgAlias(token: string): string | null {
  const org = getOrganizationFromToken(token);
  return org?.alias || null;
}

/**
 * JetStream stream name derived from an org alias (e.g. "focke-test" → "FOCKE_TEST").
 * This is the general edge-data stream used for most subscriptions.
 */
export function getOrgStreamName(orgAlias: string): string {
  return orgAlias.toUpperCase().replace(/-/g, '_');
}

/**
 * JetStream stream name for Live Shift data. The `edge-data.<serial>.shift.current.*`
 * subjects are now sourced into a dedicated `<ORG>_SHIFT` stream (e.g.
 * "FOCKE_TEST_SHIFT") rather than the general `<ORG>` stream.
 */
export function getShiftStreamName(orgAlias: string): string {
  return `${getOrgStreamName(orgAlias)}_SHIFT`;
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const claims = decodeToken(token);
  if (!claims || !claims.exp) {
    return true;
  }
  
  // exp is in seconds, Date.now() is in milliseconds
  return Date.now() >= claims.exp * 1000;
}

/**
 * Get token expiration time in milliseconds
 */
export function getTokenExpirationTime(token: string): number | null {
  const claims = decodeToken(token);
  if (!claims || !claims.exp) {
    return null;
  }
  return claims.exp * 1000;
}
